import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import type { Utilisateur } from '@/shared/auth/types';
import { useAuth } from '@/shared/auth/contexte';
import { ModaleIdentite } from '@/shared/comptes/Identite';
import { Avatar, cn } from '@/shared/ui';
import { accesDe, voitCommeCeo } from '@/shared/auth/droits';
import { navigationPour } from '../navigation';
import { libelleRole } from './libelles';

export function Sidebar({
  utilisateur,
  onNaviguer,
  className,
}: {
  utilisateur: Utilisateur;
  onNaviguer?: () => void;
  className?: string;
}) {
  const { service, relire } = useAuth();
  const [profil, setProfil] = useState(false);
  const groupes = navigationPour(accesDe(utilisateur), voitCommeCeo(utilisateur));

  return (
    <aside
      className={cn(
        'flex h-full w-64 flex-col gap-6 rounded-[14px] border border-nav-border bg-nav p-4 shadow-card',
        className
      )}
    >
      <NavLink to="/" onClick={onNaviguer} className="flex items-end gap-3 px-1 pt-1">
        {/* Le logo est vert sur fond clair : on le passe en blanc sur le plan sombre. */}
        <img src="/logo-vla.svg" alt="VLA Énergie" className="h-11 w-auto brightness-0 invert" />
        <span className="border-l border-nav-border pb-1 pl-3 text-[11px] font-semibold tracking-[0.08em] text-nav-muted uppercase">
          Logistique
        </span>
      </NavLink>

      <nav
        aria-label="Navigation principale"
        className="flex flex-1 flex-col gap-6 overflow-y-auto"
      >
        {groupes.map((groupe) => (
          <div key={groupe.libelle} className="flex flex-col gap-0.5">
            <span className="px-3 pb-1.5 text-[11px] font-semibold tracking-[0.06em] text-nav-muted uppercase">
              {groupe.libelle}
            </span>
            {groupe.entrees.map((entree) => {
              const Icone = entree.icone;
              return (
                <NavLink
                  key={entree.id}
                  to={entree.chemin}
                  onClick={onNaviguer}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 rounded-[10px] px-3 py-2.5 text-[13px] font-medium transition',
                      isActive
                        ? 'bg-brand-nav text-white shadow-[0_4px_14px_rgb(0_0_0/0.25),inset_0_1px_0_rgb(255_255_255/0.18)]'
                        : 'text-nav-text hover:bg-nav-hover hover:text-nav-text-strong'
                    )
                  }
                >
                  <Icone className="size-[18px]" aria-hidden />
                  <span className="flex-1 truncate">{entree.libelle}</span>
                  {entree.prevuAu ? (
                    <span className="rounded-full bg-nav-tile px-1.5 text-[10px] font-semibold text-nav-muted">
                      {entree.prevuAu}
                    </span>
                  ) : null}
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>

      <div className="flex items-center gap-2.5 border-t border-nav-border pt-3">
        <button
          type="button"
          onClick={() => {
            setProfil(true);
          }}
          className="-m-1 flex min-w-0 flex-1 items-center gap-2.5 rounded-lg p-1 text-left hover:bg-nav-hover"
          title="Mon profil"
        >
          <Avatar
            nom={utilisateur.nom || utilisateur.email}
            photo={utilisateur.photo ?? utilisateur.partenaireLogo}
            className="bg-nav-tile text-nav-text-strong"
          />
          <span className="min-w-0 flex-1">
            <strong className="block truncate text-xs text-nav-text-strong">
              {utilisateur.nom || utilisateur.email}
            </strong>
            <span className="block truncate text-[11px] text-nav-muted">
              {libelleRole(utilisateur)}
            </span>
          </span>
        </button>
        <button
          type="button"
          onClick={() => void service.deconnexion()}
          className="grid size-8 place-items-center rounded-lg text-nav-text hover:bg-nav-hover hover:text-nav-text-strong"
          aria-label="Se déconnecter"
          title="Se déconnecter"
        >
          <LogOut className="size-4" aria-hidden />
        </button>
      </div>
      {profil ? (
        <ModaleIdentite
          compte={utilisateur}
          soi
          onFermer={() => {
            setProfil(false);
          }}
          onFait={relire}
        />
      ) : null}
    </aside>
  );
}
